// src/features/system/devices/components/iot/IoTDashboardPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// At-a-glance overview of all registered devices: online/offline presence,
// latest telemetry snapshot (IoT pivot), and a quick-status table.
//
// Data sources:
//   • Device list  → GET /bff/device-ops/devices
//   • Presence     → GET /bff/device-ops/presence
//   • Snapshot     → GET /bff/orchestrator/hot-stream/snapshot
// ────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PresenceDot from '@/shared/components/ui/PresenceDot';
import {
  fetchPresenceApi,
  fetchTelemetrySnapshotApi,
  DEVICE_KEYS,
  type Device,
  type DevicePresence,
  type TelemetrySnapshot,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';

// ── Helpers ──────────────────────────────────────────────────────────────

function timeAgo(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const ms = Date.now() - new Date(value).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  } catch {
    return value ?? '—';
  }
}


/** Merge device + presence + snapshot into a single dashboard row. */
interface DashboardRow {
  device_id:   string;
  label:       string | null;
  device_type: string | null;
  is_active:   boolean;
  online:      boolean;
  last_seen:   string | null;
  temperature: number | null;
  humidity:    number | null;
  rssi:        number | null;
  battery:     number | null;
}

function buildRows(
  devices: Device[],
  presenceMap: Map<string, DevicePresence>,
  snapMap: Map<string, TelemetrySnapshot>,
): DashboardRow[] {
  return devices.map(d => {
    const p = presenceMap.get(d.device_id);
    const s = snapMap.get(d.device_id);
    return {
      device_id:   d.device_id,
      label:       d.label ?? null,
      device_type: d.device_type ?? null,
      is_active:   d.is_active,
      online:      p?.online ?? false,
      last_seen:   p?.last_seen ?? d.last_seen_at ?? null,
      temperature: s?.temperature ?? null,
      humidity:    s?.humidity ?? null,
      rssi:        s?.rssi ?? null,
      battery:     s?.battery ?? null,
    };
  });
}

// ── Component ────────────────────────────────────────────────────────────

export default function IoTDashboardPanel() {
  // 1 Hz tick — keeps the "Last Seen" relative times live without re-fetching.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 1_000);
    return () => clearInterval(id);
  }, []);

  const { data: devices = [], isLoading, error } = useDevicesList({ refetchInterval: 30_000 });

  const { data: presence = [] } = useQuery({
    queryKey: DEVICE_KEYS.presence,
    queryFn: fetchPresenceApi,
    refetchInterval: 10_000,
  });

  const { data: snapshots = [] } = useQuery({
    queryKey: DEVICE_KEYS.snapshot,
    queryFn: fetchTelemetrySnapshotApi,
    refetchInterval: 30_000,
  });

  const presenceMap = new Map(presence.map(p => [p.device_id, p]));
  const snapMap     = new Map(snapshots.map(s => [s.scope_key, s]));
  const rows        = buildRows(devices, presenceMap, snapMap);

  const onlineCount  = rows.filter(r => r.online).length;
  const offlineCount = rows.length - onlineCount;
  const activeCount  = rows.filter(r => r.is_active).length;

  const columns: ColumnDef<DashboardRow>[] = [
    {
      header: '',
      accessor: 'online',
      Cell: (v: boolean) => <PresenceDot online={Boolean(v)} />,
      sortable: false,
    },
    { header: 'Device ID', accessor: 'device_id' },
    { header: 'Label',     accessor: 'label',       Cell: (v: string | null) => v ?? '—' },
    { header: 'Type',      accessor: 'device_type', Cell: (v: string | null) => v ?? '—' },
    {
      header: 'Temp',
      accessor: 'temperature',
      Cell: (v: number | null) => v !== null ? `${v.toFixed(1)} °C` : '—',
    },
    {
      header: 'Humidity',
      accessor: 'humidity',
      Cell: (v: number | null) => v !== null ? `${v.toFixed(0)} %` : '—',
    },
    {
      header: 'RSSI',
      accessor: 'rssi',
      Cell: (v: number | null) => v !== null ? `${v} dBm` : '—',
    },
    {
      header: 'Battery',
      accessor: 'battery',
      Cell: (v: number | null) => v !== null ? `${v}%` : '—',
    },
    {
      header: 'Last Seen',
      accessor: 'last_seen',
      Cell: (v: string | null) => (
        <span className="text-neutral-400 text-xs">{timeAgo(v)}</span>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      {/* Summary cards */}
      <div className="flex gap-3 shrink-0">
        <SummaryCard label="Total"   value={rows.length} />
        <SummaryCard label="Active"  value={activeCount} accent="sky" />
        <SummaryCard label="Online"  value={onlineCount}  accent="emerald" />
        <SummaryCard label="Offline" value={offlineCount} accent="neutral" />
      </div>

      {/* Device table */}
      <div className="flex-1 min-h-0 overflow-auto">
        {error ? (
          <div className="text-red-400 text-sm p-2">Error: {(error as Error).message}</div>
        ) : (
          <MinimalisticTable<DashboardRow>
            columns={columns}
            data={rows}
            noDataMessage={isLoading ? 'Loading devices…' : 'No devices registered yet.'}
            showNewButton={false}
          />
        )}
      </div>
    </div>
  );
}

// ── Summary card ─────────────────────────────────────────────────────────

function SummaryCard({ label, value, accent = 'sky' }: {
  label: string;
  value: number;
  accent?: 'sky' | 'emerald' | 'neutral';
}) {
  const ring: Record<string, string> = {
    sky:     'border-sky-500/30',
    emerald: 'border-emerald-500/30',
    neutral: 'border-neutral-700',
  };
  const text: Record<string, string> = {
    sky:     'text-sky-400',
    emerald: 'text-emerald-400',
    neutral: 'text-neutral-500',
  };
  return (
    <div className={`px-4 py-2 rounded-lg border ${ring[accent]} pac-glass`}>
      <div className={`text-2xl font-bold tabular-nums ${text[accent]}`}>{value}</div>
      <div className="text-[11px] text-neutral-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
